import { type AuraDef, auraOf } from './auras';
import { DUMMY_MONSTERS } from './dummies';
import { DUNGEON_MONSTERS } from './dungeon';
import { WORLD_BOSS_MONSTERS } from './worldBosses';
import { SMALL_DUNGEON_MONSTERS } from './smallDungeons';

export interface MonsterDef {
  readonly id: string;
  readonly name: string;
  readonly level: number;
  readonly hp: number;
  readonly atk: number;
  readonly def: number;
  readonly speed: number;
  /** Distance (tiles) at which an idle monster notices the player. */
  readonly aggro: number;
  /** Distance from spawn after which the monster gives up and returns home. */
  readonly leash: number;
  /** Seconds between combat decisions. */
  readonly decisionRate: number;
  readonly attack: string;
  readonly break?: string;
  readonly special?: string;
  readonly canBlock: boolean;
  readonly reward: { readonly cultivation: number; readonly gold: readonly [number, number] };
  /** Equipment that may drop, each rolled independently. */
  readonly loot?: readonly { readonly item: string; readonly chance: number }[];
  /** Seconds until respawn; null for unique bosses. */
  readonly respawn: number | null;
  readonly sprite: string;
  readonly scale: number;
  /** A boss's aura (data/auras), drawn round it while it lives. */
  readonly aura?: AuraDef;
}

const list: MonsterDef[] = [
  {
    id: 'wolf',
    name: 'หมาป่าเงาจันทร์',
    level: 2,
    hp: 90,
    atk: 11,
    def: 5,
    speed: 2.7,
    aggro: 4,
    leash: 9,
    decisionRate: 1.1,
    attack: 'wolf-bite',
    canBlock: false,
    reward: { cultivation: 8, gold: [2, 6] },
    loot: [{ item: 'wolf-pelt', chance: 0.12 }],
    respawn: 12,
    sprite: 'wolf',
    scale: 1,
  },
  {
    id: 'bandit',
    name: 'โจรภูเขา',
    level: 4,
    hp: 150,
    atk: 14,
    def: 10,
    speed: 2.2,
    aggro: 4.5,
    leash: 9,
    decisionRate: 1.25,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 14, gold: [6, 14] },
    loot: [
      { item: 'red-band', chance: 0.12 },
      { item: 'bamboo-hat', chance: 0.06 },
      { item: 'azure-sword', chance: 0.04 },
      { item: 'black-vest', chance: 0.04 },
    ],
    respawn: 12,
    sprite: 'bandit',
    scale: 1,
  },
  {
    // คนหน้ากากไม้ (docs/design/C0_OPENING_PLAINS.md §11.3, M7): the hunters of the marked at the bamboo edge of the
    // 256 m plains, in pairs; a bandit's kit (guard, a charged break), danger tier 2. Spawned by systems/plainsStory.ts.
    id: 'masked',
    name: 'คนหน้ากากไม้',
    level: 5,
    hp: 170,
    atk: 15,
    def: 10,
    speed: 2.3,
    aggro: 6,
    leash: 12,
    decisionRate: 1.2,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 14, gold: [6, 14] },
    respawn: 30,
    sprite: 'maskedMan',
    scale: 1,
  },
  {
    // หัวหน้าหน้ากากไม้ (M7's mini-boss, tier 3): guards often and charges a two-hand break; calls two masks at half
    // health (systems/plainsStory.ts).
    id: 'masked-chief',
    name: 'หัวหน้าหน้ากากไม้',
    level: 7,
    hp: 1100,
    atk: 19,
    def: 14,
    speed: 2.4,
    aggro: 7,
    leash: 14,
    decisionRate: 1.05,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 60, gold: [30, 50] },
    respawn: 360,
    sprite: 'maskedChief',
    scale: 1.2,
  },
  {
    // The training yard of Yanyu (docs/maps/YANYU.md): village lads who spar with newcomers. They
    // never start a fight and hit softly, but strike, guard and gather a break like a bandit, so the
    // three forms can be learned in safety.
    id: 'sparrer',
    name: 'ศิษย์คู่ซ้อม',
    level: 1,
    hp: 140,
    atk: 3,
    def: 6,
    speed: 2,
    aggro: 0,
    leash: 6,
    decisionRate: 1.3,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 4, gold: [0, 1] },
    respawn: 6,
    sprite: 'watchman',
    scale: 1,
  },
  {
    // The 3D training yard's sparring partner (tools/yard3d): the yard lad's moves, look and manners, sturdy enough
    // for a bout of 30–45 s at a new character's damage (about 35 a second), so the triangle can be practised at length.
    id: 'sparrer-yard',
    name: 'ศิษย์พี่ฉีหมิง',
    level: 1,
    hp: 1200,
    atk: 3,
    def: 6,
    speed: 2,
    aggro: 0,
    leash: 6,
    decisionRate: 1.3,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 4, gold: [0, 1] },
    respawn: 6,
    sprite: 'watchman',
    scale: 1,
  },
  {
    id: 'assassin',
    name: 'นักฆ่าเงาดำ',
    level: 6,
    hp: 140,
    atk: 17,
    def: 8,
    speed: 3.2,
    aggro: 5,
    leash: 10,
    decisionRate: 0.8,
    attack: 'assassin-stab',
    break: 'assassin-feint',
    canBlock: false,
    reward: { cultivation: 20, gold: [10, 18] },
    loot: [{ item: 'black-vest', chance: 0.06 }],
    respawn: 14,
    sprite: 'assassin',
    scale: 1,
  },
  {
    id: 'monk',
    name: 'พระนอกรีต',
    level: 7,
    hp: 260,
    atk: 15,
    def: 18,
    speed: 1.9,
    aggro: 4,
    leash: 10,
    decisionRate: 1.3,
    attack: 'monk-staff',
    break: 'monk-sweep',
    canBlock: true,
    reward: { cultivation: 24, gold: [8, 16] },
    loot: [{ item: 'bamboo-hat', chance: 0.08 }],
    respawn: 14,
    sprite: 'rogueMonk',
    scale: 1,
  },
  {
    id: 'bear',
    name: 'หมีดำภูเขา',
    level: 8,
    hp: 420,
    atk: 22,
    def: 14,
    speed: 2.0,
    aggro: 4,
    leash: 10,
    decisionRate: 1.5,
    attack: 'bear-swipe',
    break: 'bear-maul',
    canBlock: false,
    reward: { cultivation: 30, gold: [4, 10] },
    loot: [{ item: 'wolf-pelt', chance: 0.2 }],
    respawn: 14,
    sprite: 'bear',
    scale: 1.4,
  },
  {
    id: 'knifer',
    name: 'โจรมีดบิน',
    level: 5,
    hp: 120,
    atk: 12,
    def: 8,
    speed: 2.2,
    aggro: 6,
    leash: 10,
    decisionRate: 1.4,
    attack: 'knife-throw',
    canBlock: false,
    reward: { cultivation: 16, gold: [8, 14] },
    loot: [{ item: 'red-band', chance: 0.1 }],
    respawn: 14,
    sprite: 'knifer',
    scale: 1,
  },
  {
    id: 'jiangshi',
    name: 'ผีกระโดด',
    level: 7,
    hp: 220,
    atk: 16,
    def: 12,
    speed: 1.6,
    aggro: 4,
    leash: 10,
    decisionRate: 1.2,
    attack: 'jiangshi-claw',
    canBlock: false,
    reward: { cultivation: 24, gold: [6, 14] },
    loot: [{ item: 'jade-crown', chance: 0.03 }],
    respawn: 14,
    sprite: 'jiangshi',
    scale: 1,
  },
  {
    id: 'snowwolf',
    name: 'หมาป่าหิมะ',
    level: 9,
    hp: 260,
    atk: 22,
    def: 12,
    speed: 3.0,
    aggro: 5,
    leash: 10,
    decisionRate: 1.0,
    attack: 'wolf-bite',
    canBlock: false,
    reward: { cultivation: 32, gold: [8, 16] },
    loot: [{ item: 'wolf-pelt', chance: 0.18 }],
    respawn: 14,
    sprite: 'snowwolf',
    scale: 1.1,
  },
  {
    id: 'frostman',
    name: 'ศิษย์สำนักหิมะทรยศ',
    level: 10,
    hp: 320,
    atk: 20,
    def: 18,
    speed: 2.3,
    aggro: 4.5,
    leash: 10,
    decisionRate: 1.15,
    attack: 'bandit-slash',
    break: 'bandit-crush',
    canBlock: true,
    reward: { cultivation: 38, gold: [14, 26] },
    loot: [
      { item: 'azure-sword', chance: 0.06 },
      { item: 'jade-crown', chance: 0.03 },
    ],
    respawn: 14,
    sprite: 'frostman',
    scale: 1,
  },
  {
    id: 'boar',
    name: 'หมูป่าเขี้ยวเหล็ก',
    level: 3,
    hp: 140,
    atk: 12,
    def: 9,
    speed: 2.4,
    aggro: 4,
    leash: 10,
    decisionRate: 1.3,
    attack: 'boar-gore',
    break: 'boar-charge',
    canBlock: false,
    reward: { cultivation: 12, gold: [3, 8] },
    loot: [{ item: 'red-band', chance: 0.05 }],
    respawn: 14,
    sprite: 'boar',
    scale: 1,
  },
  {
    id: 'viper',
    name: 'งูพิษหยก',
    level: 4,
    hp: 110,
    atk: 13,
    def: 6,
    speed: 2.2,
    aggro: 4,
    leash: 10,
    decisionRate: 1.1,
    attack: 'viper-fang',
    canBlock: false,
    reward: { cultivation: 14, gold: [3, 9] },
    respawn: 14,
    sprite: 'viper',
    scale: 1,
  },
  {
    id: 'scorpion',
    name: 'แมงป่องหางเพลิง',
    level: 5,
    hp: 170,
    atk: 14,
    def: 14,
    speed: 1.9,
    aggro: 4,
    leash: 10,
    decisionRate: 1.3,
    attack: 'scorpion-pincer',
    break: 'scorpion-sting',
    canBlock: false,
    reward: { cultivation: 18, gold: [5, 11] },
    loot: [{ item: 'black-vest', chance: 0.04 }],
    respawn: 14,
    sprite: 'scorpion',
    scale: 1,
  },
  {
    id: 'leopard',
    name: 'เสือดาวเงา',
    level: 6,
    hp: 180,
    atk: 17,
    def: 9,
    speed: 3.1,
    aggro: 5,
    leash: 10,
    decisionRate: 1.0,
    attack: 'leopard-claw',
    canBlock: false,
    reward: { cultivation: 22, gold: [6, 12] },
    loot: [{ item: 'wolf-pelt', chance: 0.12 }],
    respawn: 14,
    sprite: 'leopard',
    scale: 1,
  },
  {
    id: 'bloodbat',
    name: 'ค้างคาวโลหิต',
    level: 6,
    hp: 130,
    atk: 15,
    def: 6,
    speed: 3.2,
    aggro: 5,
    leash: 10,
    decisionRate: 0.95,
    attack: 'bat-fang',
    canBlock: false,
    reward: { cultivation: 20, gold: [4, 10] },
    respawn: 14,
    sprite: 'bloodBat',
    scale: 1,
  },
  {
    id: 'spider',
    name: 'แมงมุมหน้าผี',
    level: 7,
    hp: 230,
    atk: 16,
    def: 12,
    speed: 2.0,
    aggro: 4,
    leash: 10,
    decisionRate: 1.2,
    attack: 'spider-fang',
    break: 'spider-web',
    canBlock: false,
    reward: { cultivation: 26, gold: [6, 14] },
    loot: [{ item: 'black-vest', chance: 0.05 }],
    respawn: 14,
    sprite: 'spider',
    scale: 1,
  },
  {
    id: 'centipede',
    name: 'ตะขาบพิษร้อยขา',
    level: 8,
    hp: 300,
    atk: 19,
    def: 15,
    speed: 2.2,
    aggro: 4,
    leash: 10,
    decisionRate: 1.15,
    attack: 'centipede-bite',
    canBlock: false,
    reward: { cultivation: 30, gold: [8, 16] },
    respawn: 14,
    sprite: 'centipede',
    scale: 1,
  },
  {
    id: 'python',
    name: 'งูเหลือมยักษ์',
    level: 8,
    hp: 460,
    atk: 21,
    def: 14,
    speed: 1.7,
    aggro: 4,
    leash: 10,
    decisionRate: 1.5,
    attack: 'python-strike',
    break: 'python-coil',
    canBlock: false,
    reward: { cultivation: 34, gold: [8, 18] },
    loot: [{ item: 'navy-coat', chance: 0.05 }],
    respawn: 14,
    sprite: 'python',
    scale: 1.15,
  },
  {
    id: 'foxspirit',
    name: 'จิ้งจอกเก้าหาง',
    level: 9,
    hp: 300,
    atk: 22,
    def: 12,
    speed: 2.9,
    aggro: 5,
    leash: 10,
    decisionRate: 1.05,
    attack: 'fox-claw',
    break: 'fox-charm',
    canBlock: false,
    reward: { cultivation: 36, gold: [10, 20] },
    loot: [{ item: 'jade-crown', chance: 0.04 }],
    respawn: 14,
    sprite: 'foxSpirit',
    scale: 1,
  },
  {
    id: 'whitetiger',
    name: 'พยัคฆ์ขาวภูผา',
    level: 11,
    hp: 620,
    atk: 26,
    def: 16,
    speed: 2.6,
    aggro: 5,
    leash: 10,
    decisionRate: 1.2,
    attack: 'tiger-claw',
    break: 'tiger-pounce',
    canBlock: false,
    reward: { cultivation: 60, gold: [18, 32] },
    loot: [
      { item: 'wolf-pelt', chance: 0.3 },
      { item: 'azure-sword', chance: 0.05 },
    ],
    respawn: 14,
    sprite: 'whiteTiger',
    scale: 1.2,
  },
  {
    id: 'boss',
    name: 'ปีศาจกระบี่ทมิฬ',
    level: 12,
    hp: 1800,
    atk: 26,
    def: 25,
    speed: 2.5,
    aggro: 7,
    leash: 12,
    decisionRate: 1.2,
    attack: 'boss-slash',
    break: 'boss-cleave',
    special: 'boss-ring',
    canBlock: true,
    reward: { cultivation: 300, gold: [300, 300] },
    loot: [
      { item: 'crimson-sword', chance: 1 },
      { item: 'crimson-coat', chance: 1 },
      { item: 'jade-crown', chance: 0.5 },
    ],
    respawn: null,
    sprite: 'boss',
    scale: 2,
    // Black smoke with a dull crimson heart: a demon of the dark sword.
    aura: auraOf('demon-blood', 1),
  },
  {
    id: 'worldboss',
    name: 'จอมมารพันกระบี่',
    level: 25,
    hp: 7000,
    atk: 48,
    def: 32,
    speed: 2.6,
    aggro: 8,
    leash: 14,
    decisionRate: 1.0,
    attack: 'boss-slash',
    break: 'boss-cleave',
    special: 'wb-rain',
    canBlock: true,
    reward: { cultivation: 1200, gold: [600, 900] },
    // Drops a กระบี่มารพันกระบี่ with rolled stats (see World.kill).
    respawn: 600,
    sprite: 'worldBoss',
    scale: 2.4,
    // The thousand-sword demon lord: wider, its heart burning brighter.
    aura: auraOf('demon-blood', 2),
  },
  // ถ้ำคลื่นคำราม (data/dungeon.ts): the Wave Mask pirates, the mini-boss and the boss.
  ...DUNGEON_MONSTERS,
  // บอสโลก (data/worldBosses.ts): ลิงยักษ์, นกเพลิง, เต่าคะนอง.
  ...WORLD_BOSS_MONSTERS,
  ...SMALL_DUNGEON_MONSTERS,
  // หุ่นไม้ (data/dummies.ts): training dummies that never fall.
  ...DUMMY_MONSTERS,
];

export const MONSTERS: Readonly<Record<string, MonsterDef>> = Object.fromEntries(list.map((m) => [m.id, m]));

export function getMonster(id: string): MonsterDef {
  const m = MONSTERS[id];
  if (!m) throw new Error(`Unknown monster: ${id}`);
  return m;
}

/**
 * The wuxia village's quest boar (client v1 hub, data/starter.ts `STARTER.monster`): the shared boar for one fresh
 * player: HP ×0.5, aggro and leash ×1.5 (the village reads metres where the boar's row reads tiles). Kept out of
 * `MONSTERS` so the other zones are untouched; the village reads it through `STARTER_MONSTERS`.
 */
export const BOAR_STARTER: MonsterDef = (() => {
  const boar = MONSTERS.boar!;
  return { ...boar, id: 'boar-starter', hp: Math.round(boar.hp * 0.5), aggro: boar.aggro * 1.5, leash: boar.leash * 1.5 };
})();

/** The village's monsters by id. */
export const STARTER_MONSTERS: Readonly<Record<string, MonsterDef>> = { [BOAR_STARTER.id]: BOAR_STARTER };
